/**
 * Feature flag keys that are registered in the application
 * This is the source of truth for all feature flags
 */
export const FEATURE_FLAGS = {
  "test-flag": "test-flag",
  "associate-product-v2-enabled": "associate-product-v2-enabled",
  "show-email-po-metrics-enabled": "show-email-po-metrics-enabled",
  "sales-order-upload-enabled": "sales-order-upload-enabled",
  "inventory-validation-enabled": "inventory-validation-enabled",
  "location-fuzzy-matching-enabled": "location-fuzzy-matching-enabled",
  "sales-order-drafts-ebs-org-filter-enabled":
    "sales-order-drafts-ebs-org-filter-enabled",
  "activity-log-enabled": "activity-log-enabled",
  "one-to-one-product-variant-mapping-enabled":
    "one-to-one-product-variant-mapping-enabled",
  "agent-monitor-enabled": "agent-monitor-enabled",
  "ext-system-references-enabled": "ext-system-references-enabled",
  "msft-office-viewer-enabled": "msft-office-viewer-enabled",
  "chat-agent-enabled": "chat-agent-enabled",
  "orpc-batching-enabled": "orpc-batching-enabled",
  "item-master-ui-enabled": "item-master-ui-enabled",
  "rebate-two-stage-extraction-enabled": "rebate-two-stage-extraction-enabled",
  "inbox-enabled": "inbox-enabled",
  "data-sources-enabled": "data-sources-enabled",
  // Gates ABAC resource-privilege enforcement per organization. When off,
  // privilege filters are logged but not applied. Falls back to the
  // RESOURCE_PRIVILEGE_ENFORCEMENT_ENABLED env var when PostHog is unavailable.
  "resource-privilege-enforcement-enabled":
    "resource-privilege-enforcement-enabled",
  "tools-page-file-extraction-enabled": "tools-page-file-extraction-enabled",
  // Kill switch for the Agents / Tasks / Tools / Workflows growth teaser
  // tabs. When ON, signup-only header entries are hidden and Workflows /
  // Agents act as they did before the teaser pages shipped. Absent/OFF keeps
  // the teaser tabs (the default since launch).
  "hide-feature-signups": "hide-feature-signups",
} as const;

/**
 * System-level feature flags evaluated against the service identity
 * rather than individual users. These flags control backend infrastructure behavior.
 */
export const SYSTEM_FEATURE_FLAGS = {
  "ext-system-references-enabled": "ext-system-references-enabled",
  "rebate-two-stage-extraction-enabled": "rebate-two-stage-extraction-enabled",
} as const;

export type SystemFeatureFlagKey =
  (typeof SYSTEM_FEATURE_FLAGS)[keyof typeof SYSTEM_FEATURE_FLAGS];

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

// Payload type for each feature flag
export type FeatureFlagConfig = {
  "test-flag": boolean;
  "associate-product-v2-enabled": boolean;
  "show-email-po-metrics-enabled": boolean;
  "sales-order-upload-enabled": boolean;
  "inventory-validation-enabled": boolean;
  "location-fuzzy-matching-enabled": boolean;
  "sales-order-drafts-ebs-org-filter-enabled": boolean;
  "activity-log-enabled": boolean;
  "one-to-one-product-variant-mapping-enabled": boolean;
  "agent-monitor-enabled": boolean;
  "ext-system-references-enabled": boolean;
  "msft-office-viewer-enabled": boolean;
  "chat-agent-enabled": boolean;
  "orpc-batching-enabled": boolean;
  "item-master-ui-enabled": boolean;
  "rebate-two-stage-extraction-enabled": boolean;
  "inbox-enabled": boolean;
  "data-sources-enabled": boolean;
  "resource-privilege-enforcement-enabled": boolean;
  "tools-page-file-extraction-enabled": boolean;
  "hide-feature-signups": boolean;
};

// Generic type for feature flag information
export type FlagInfo<Key extends FeatureFlagKey> = {
  enabled: boolean;
  variantKey?: string;
  payload?: FeatureFlagConfig[Key];
};
